import React from "react"
import PropTypes from "prop-types"

/**
 * ActionIcon Component
 * A reusable action icon component with consistent styling
 * 
 * @param {string} type - Icon type (view, edit, delete, send-verification)
 * @param {function} onClick - Click handler function
 * @param {string} icon - MDI icon class (optional, defaults based on type)
 * @param {string} tooltip - Tooltip text (optional)
 * @param {boolean} disabled - Whether the icon is disabled
 * @param {string} className - Additional CSS classes
 */
const ActionIcon = ({
  type = "view",
  onClick,
  icon,
  tooltip,
  disabled = false,
  className = "",
}) => {
  const iconMap = {
    view: "mdi mdi-eye",
    edit: "mdi mdi-pencil",
    delete: "mdi mdi-delete",
    "send-verification": "mdi mdi-card-account-mail",
  }
  
  const iconClass = icon || iconMap[type] || "mdi mdi-eye"
  const disabledClass = disabled ? "no-access" : ""
  
  const handleClick = (e) => {
    if (!disabled && onClick) {
      e.stopPropagation()
      onClick(e)
    }
  }
  
  return (
    <span
      className={`action-icon ${type} ${disabledClass} ${className}`}
      onClick={handleClick}
      title={tooltip}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyPress={(e) => {
        if (e.key === "Enter" && !disabled) {
          handleClick(e)
        }
      }}
    >
      <i className={iconClass} />
    </span>
  )
}

/**
 * ActionIcons Component
 * A container for multiple action icons
 * 
 * @param {array} actions - Array of action objects
 * @param {string} className - Additional CSS classes
 */
const ActionIcons = ({ actions = [], className = "" }) => {
  if (!actions || actions.length === 0) return null
  
  return (
    <div className={`action-icons ${className}`}>
      {actions.map((action, index) => (
        <ActionIcon key={index} {...action} />
      ))}
    </div>
  )
}

ActionIcon.propTypes = {
  type: PropTypes.oneOf(["view", "edit", "delete", "send-verification"]),
  onClick: PropTypes.func,
  icon: PropTypes.string,
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

ActionIcons.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.string,
      tooltip: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ),
  className: PropTypes.string,
}

export { ActionIcon }
export default ActionIcons
